import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useUsageTracking } from 'crm-object-board-core/usageTracking';
import { useLaunchCSAT } from 'crm-object-board-core/csat';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const StoreContext = /*#__PURE__*/createContext(null);
const useCardSettingsPanelStore = () => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useCardSettingsPanelStore must be inside CardSettingsPanelProvider');
  }
  return store;
};

// Providing the card settings panel actions and rendering the panel

export function CardSettingsPanelProvider(_ref) {
  let {
      children
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  let alreadyHasContext = true;
  try {
    useCardSettingsPanelStore();
  } catch (e) {
    alreadyHasContext = false;
  }
  if (alreadyHasContext) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(InternalCardSettingsPanelProvider, Object.assign({}, props, {
    children: children
  }));
}
function InternalCardSettingsPanelProvider({
  children,
  defaultView = null,
  onClose
}) {
  const launchCSAT = useLaunchCSAT();
  const {
    trackAdminOpenSettings,
    trackUserOpenSettings
  } = useUsageTracking();
  const [cardSettingsView, setCardSettingsView] = useState(defaultView);
  const openCardSettings = useCallback((view, source = 'other') => {
    if (view === 'admin') {
      trackAdminOpenSettings(source);
    } else {
      trackUserOpenSettings(source);
    }
    setCardSettingsView(view);
  }, [trackAdminOpenSettings, trackUserOpenSettings]);
  const closeCardSettings = useCallback(() => {
    setCardSettingsView(null);
    launchCSAT(cardSettingsView === 'admin' ? 'adminClosedSettings' : 'userClosedSettings');
    onClose === null || onClose === void 0 || onClose();
  }, [onClose, launchCSAT, cardSettingsView]);
  const value = useMemo(() => ({
    cardSettingsView,
    openCardSettings,
    onClose: closeCardSettings
  }), [cardSettingsView, openCardSettings, closeCardSettings]);
  return /*#__PURE__*/_jsx(StoreContext.Provider, {
    value: value,
    children: /*#__PURE__*/_jsx(_Fragment, {
      children: children
    })
  });
}
export const useOpenCardSettingsPanel = () => {
  const store = useCardSettingsPanelStore();
  return store.openCardSettings;
};
export const useIsCardSettingsPanelOpen = () => {
  const store = useCardSettingsPanelStore();
  return store.cardSettingsView !== null;
};
export const useCardSettingsOnClose = () => {
  const store = useCardSettingsPanelStore();
  return store.onClose;
};
export const useIsAdminView = () => {
  const store = useCardSettingsPanelStore();
  return store.cardSettingsView === 'admin';
};